import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Minimize2 } from 'lucide-react';
import { API_ENDPOINTS } from '../../../config/api.config';
import { apiService } from '../../../services/apiService';

interface Message {
    text: string;
    sender: 'user' | 'assistant';
    timestamp: Date;
}

interface ChatApplication {
    id: string;
    name: string;
}

// interface ChatResponse {
//     response: string;
// }

// interface ChatHistory {
//     role: 'user' | 'assistant';
//     content: string;
// }

// interface ChatRequestPayload {
//     query: string;
//     history: ChatHistory[];
//     userId: string | null;
// }

const ChatPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const decodedId = id ?? '';
    const userId = localStorage.getItem('userId');
    const sessionKey = `chat_${decodedId}_${userId}`;

    const [messages, setMessages] = useState<Message[]>([]);
    // const [input, setInput] = useState('');
    const [appName, setAppName] = useState('');
    // const [isLoading, setIsLoading] = useState(false);

    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Load saved messages and app details
    useEffect(() => {
        if (!decodedId || !userId) {
            navigate('/');
            return;
        }

        const loadInitialData = async () => {
            // Load saved messages
            const savedMessages = sessionStorage.getItem(sessionKey);
            if (savedMessages) {
                setMessages(JSON.parse(savedMessages));
            }

            // Fetch app details
            try {
                const data = await apiService.get<ChatApplication>(
                    `${API_ENDPOINTS.RAG}/get/${decodedId}`
                );
                setAppName(data.name);
                document.title = `Chat - ${data.name}`;
            } catch (error) {
                console.error('Failed to fetch app details:', error);
                navigate('/');
            }
        };

        loadInitialData();

        // Save messages to session storage on unmount
        return () => {
            sessionStorage.setItem(sessionKey, JSON.stringify(messages));
        };
    }, [decodedId, userId, sessionKey, navigate]);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // const handleSend = async (e: React.FormEvent<HTMLFormElement>) => {
    //     e.preventDefault();
    //     if (!input.trim() || isLoading) return;
    //
    //     const newMessage: Message = {
    //         text: input.trim(),
    //         sender: 'user',
    //         timestamp: new Date()
    //     };
    //
    //     setIsLoading(true);
    //     setInput('');
    //
    //     const updatedMessages = [...messages, newMessage];
    //     setMessages(updatedMessages);
    //     sessionStorage.setItem(sessionKey, JSON.stringify(updatedMessages));
    //
    //     try {
    //         const payload: ChatRequestPayload = {
    //             query: newMessage.text,
    //             history: messages.map(msg => ({
    //                 role: msg.sender,
    //                 content: msg.text
    //             })),
    //             userId
    //         };
    //
    //         const data = await apiService.post<ChatResponse>(
    //             `${API_ENDPOINTS.RAG}/chat/${decodedId}`,
    //             payload
    //         );
    //
    //         const assistantMessage: Message = {
    //             text: data.response,
    //             sender: 'assistant',
    //             timestamp: new Date()
    //         };
    //
    //         const finalMessages = [...updatedMessages, assistantMessage];
    //         setMessages(finalMessages);
    //         sessionStorage.setItem(sessionKey, JSON.stringify(finalMessages));
    //     } catch (error) {
    //         console.error('Failed to send message:', error);
    //         const errorMessage: Message = {
    //             text: 'Sorry, I encountered an error. Please try again.',
    //             sender: 'assistant',
    //             timestamp: new Date()
    //         };
    //         const finalMessages = [...updatedMessages, errorMessage];
    //         setMessages(finalMessages);
    //         sessionStorage.setItem(sessionKey, JSON.stringify(finalMessages));
    //     } finally {
    //         setIsLoading(false);
    //     }
    // };

    const renderMessage = (msg: Message, idx: number) => (
        <div
            key={idx}
            className={`d-flex ${msg.sender === 'user' ? 'justify-content-end' : 'justify-content-start'} mb-3`}
        >
            <div
                className={`p-3 ${
                    msg.sender === 'user'
                        ? 'text-white rounded-4 rounded-bottom-end-0'
                        : 'bg-white shadow-sm rounded-4 rounded-bottom-start-0'
                }`}
                style={{
                    maxWidth: '70%',
                    fontSize: '0.95rem',
                    lineHeight: '1.5',
                    backgroundColor: msg.sender === 'user' ? 'rgba(170, 62, 17, 0.85)' : undefined
                }}
            >
                {msg.text}
            </div>
        </div>
    );

    return (
        <div className="vh-100 d-flex flex-column">
            {/* Minimize button */}
            <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1000 }}>
                <button
                    className="btn btn-light d-flex align-items-center justify-content-center"
                    onClick={() => window.close()}
                    style={{ width: '40px', height: '40px' }}
                    title={`Close chat with ${appName}`}
                >
                    <Minimize2 size={24} />
                </button>
            </div>

            {/* Chat container */}
            <div className="flex-grow-1 overflow-auto px-4 py-2" style={{ backgroundColor: '#fbfbfc' }}>
                <div className="container-lg mx-auto">
                    {messages.map((msg, idx) => renderMessage(msg, idx))}
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Input area */}
            {/*<div className="bg-white p-4">*/}
            {/*    <div className="container-lg mx-auto">*/}
            {/*        <form onSubmit={handleSend} className="d-flex gap-2">*/}
            {/*            <div className="position-relative flex-grow-1">*/}
            {/*                /!*<input*!/*/}
            {/*                /!*    type="text"*!/*/}
            {/*                /!*    value={input}*!/*/}
            {/*                /!*    onChange={(e) => setInput(e.target.value)}*!/*/}
            {/*                /!*    className="form-control border bg-light bg-opacity-50 rounded-3 py-2 px-3"*!/*/}
            {/*                /!*    placeholder="Type your message..."*!/*/}
            {/*                /!*    style={{ paddingRight: '3rem' }}*!/*/}
            {/*                /!*    disabled={isLoading}*!/*/}
            {/*                /!*    autoFocus*!/*/}
                            {/*/>*/}
            {/*                <button*/}
            {/*                    type="submit"*/}
            {/*                    className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-dark px-3"*/}
            {/*                    disabled={!input.trim() || isLoading}*/}
            {/*                >*/}
            {/*                    <Send*/}
            {/*                        size={18}*/}
            {/*                        style={{*/}
            {/*                            color: input.trim() && !isLoading ? '#aa3e11' : '#6c757d'*/}
            {/*                        }}*/}
            {/*                    />*/}
            {/*                </button>*/}
            {/*            </div>*/}
            {/*        </form>*/}
            {/*    </div>*/}
            {/*</div>*/}
        </div>
    );
};

export default ChatPage;